import React from "react";
import PropTypes from "prop-types";

// CLASSES
import AppConfig from "@classes/AppConfig";
import Validator from "@classes/Validator";

// COMPONENTS
import Button from "@components/Button/Button";
import Form from "@components/Form/Form";
import StackLayout from "@components/layouts/StackLayout/StackLayout";
import TextField from "@components/TextField/TextField";

// HELPERS
import {createClassName, delay} from "@helpers/utils";
import Checkbox from "../../../../components/Checkbox/Checkbox";

export default class CobStart extends React.PureComponent {

	static propTypes = {
		onLookupDataLoaded: PropTypes.func.isRequired,
	};

	state = _getInitialState.call(this);

	render() {
		const {props, state} = this;
		const {app, assentlyStatus} = props;
		const {partner} = app;
		const classes = createClassName("Cob-Start", {});

		let apiUrl = AppConfig.get("apiUrl");
		if (apiUrl === "/") {
			apiUrl = window.location.protocol + '//' + window.location.hostname;
		}

		let welcomeParagraph = partner.welcomeParagraph;
		if (welcomeParagraph) {
			welcomeParagraph = welcomeParagraph.replace(new RegExp("#API#", 'g'), apiUrl);
		}

		/**
		 * There are different modes that the start page can have. It can be for private individuals only, for private
		 * individuals and companies and for both types but with a switch to select the type we are inputting at the moment.
		 */
		let ssnTextField, ssnTextFieldLabel, ssnTextFieldDescription;
		if (state.personTypeSelected === 'individual') { // Switching types allowed and company selected
			ssnTextFieldLabel = (
				<div style={{width: "100%", maxWidth: "480px", alignItems: "flex-start", padding: "5px 0 10px 0"}}>
					<Checkbox
						label="Kontoöppningen gäller företag"
						checked={false}
						onChange={this._setCompanyType}
					/>
				</div>
			);
			ssnTextFieldDescription = (
				<p style={{margin: "5px 0 0 0", fontSize: "12px", width: "100%", maxWidth: "480px", padding: "0 5px"}}>
					För privatpersoner kommer en ISK att öppnas.
				</p>
			);
			ssnTextField = (
				<TextField
					label={"Personnummer (12 siffror)"}
					placeholder={"ÅÅÅÅMMDD-XXXX"}
					value={state.ssnOrg}
					valid={state.ssnOrg ? state.ssnOrgValid : null}
					onChange={this._onSsnOrgChange}
					onBlur={this._onSsnOrgBlur}
				/>
			);
		}
		else if (state.personTypeSelected === 'company') { // Switching types allowed and company selected
			ssnTextFieldLabel = (
				<div style={{width: "100%", maxWidth: "480px", alignItems: "flex-start", padding: "5px 0 10px 0"}}>
					<Checkbox
						label="Kontoöppningen gäller företag"
						checked={true}
						onChange={this._setCompanyType}
					/>
				</div>
			);
			ssnTextFieldDescription = (
				<p style={{margin: "5px 0 0 0", fontSize: "12px", width: "100%", maxWidth: "480px", padding: "0 5px"}}>
					För juridiska personer kommer ett depåkonto att öppnas.
				</p>
			);
			ssnTextField = (
				<TextField
					label={"Organisationsnummer (10 siffror)"}
					placeholder={"XXXXXX-XXXX"}
					value={state.ssnOrg}
					valid={state.ssnOrg ? state.ssnOrgValid : null}
					onChange={this._onSsnOrgChange}
					onBlur={this._onSsnOrgBlur}
				/>
			);
		}
		else { // Switching types not allowed
			ssnTextFieldLabel = (
				<p style={{width: "100%", textAlign: "center"}}>
					{partner.allowCompany ? "Var vänlig fyll i person- eller organisationsnummer (12 siffror)." : "Var vänlig fyll i personnummer (12 siffror)."}
				</p>
			);

			if (partner.name === 'fair-ag' || partner.name === 'glofitech-ag') {
				ssnTextFieldLabel = null;
			}

			ssnTextFieldDescription = "";
			ssnTextField = (
				<TextField
					label={partner.allowCompany ? "Person- eller organisationsnummer" : "Personnummer (12 siffror)"}
					placeholder={!partner.allowCompany ? "ÅÅÅÅMMDD-XXXX" : undefined}
					value={state.ssnOrg}
					valid={state.ssnOrg ? state.ssnOrgValid : null}
					onChange={this._onSsnOrgChange}
					onBlur={this._onSsnOrgBlur}
				/>
			)
		}

		return (
			<div className={classes}>
				<StackLayout orientation="vertical" fullWidth>
					<h1>{partner.welcomeTitle}</h1>

					{partner.welcomeSubtitle && (
						<h2>{partner.welcomeSubtitle}</h2>
					)}

					{welcomeParagraph && (
						<p className={"intro"} dangerouslySetInnerHTML={{__html: welcomeParagraph}}/>
					)}

					{ssnTextFieldLabel}

					<Form onSubmit={this._onContinue} submitOnEnter>
						{ssnTextField}

						{ssnTextFieldDescription}

						<Button
							type="submit"
							className="continue-button"
							label="Gå vidare"
							appearance={(state.ssnOrg && state.ssnOrgValid) ? "primary" : "medium"}
							busy={state.busy}
							disabled={!(state.ssnOrg && state.ssnOrgValid)}
						/>
					</Form>

					{partner.welcomeDisclaimer && (
						<p style={{
							marginTop: "20px",
							marginBottom: "0",
							fontSize: "16px",
							fontStyle: "italic",
							textAlign: "center"
						}}>
							{partner.welcomeDisclaimer}
						</p>
					)}

				</StackLayout>

				{assentlyStatus && assentlyStatus.length > 0 && (
					<div style={{margin: "40px 0 0 0", background: "#FFF5F5", width: "100%", display: "block", padding: "20px", fontSize: "13px"}}>
						<strong style={{display: "block", padding: "0 0 5px 0"}}>Driftstörning Assently (digital signering)</strong>
						För närvarande påverkas följande system: {assentlyStatus.join(", ")} <br /><br />
						<a href="https://assently.statuspage.io/" target="_blank" rel="noopener noreferrer">För mer information, klicka här</a>
					</div>
				)}
			</div>
		);
	}

	_setCompanyType = (value) => {
		if (value) {
			this.setState({
				personTypeSelected: "company"
			}, () => {
				this.setState({
					ssnOrgType: this._validateSsnOrg(this.state.ssnOrg).type,
					ssnOrgValid: this._validateSsnOrg(this.state.ssnOrg).valid ? true : null
				});
			});
		}
		else {
			this.setState({
				personTypeSelected: "individual",
			}, () => {
				this.setState({
					ssnOrgType: this._validateSsnOrg(this.state.ssnOrg).type,
					ssnOrgValid: this._validateSsnOrg(this.state.ssnOrg).valid ? true : null
				});
			});
		}
	};

	// Internal methods
	_validateSsnOrg = (value) => {
		const {partner} = this.props.app;
		value = value.replace(/-/g, "");

		if (value.length !== 12) {
			return false;
		}

		const isValidPerson = Validator.validateSsn(value, partner.allowShortSsn);
		const isValidCompany = Validator.validateOrgNum(value, partner.allowShortSsn);

		if (this.state.personTypeSelected === 'individual') {
			return {
				type: !isValidPerson ? null : "person",
				valid: value ? isValidPerson : null
			};
		}
		else if (this.state.personTypeSelected === 'company') {
			return {
				type: !isValidCompany ? null : "company",
				valid: value ? isValidCompany : null
			};
		}

		if (partner.allowCompany) {
			const isValid = isValidPerson || isValidCompany;
			return {
				type: !isValid ? null : (isValidCompany ? "company" : "person"),
				valid: value ? isValid : null
			};
		}
		else {
			const isValid = isValidPerson;
			return {
				type: !isValid ? null : (isValidPerson ? "person" : "company"),
				valid: value ? isValid : null
			};
		}
	};

	_onSsnOrgChange = (value) => {
		const ssnOrgValidated = this._validateSsnOrg(value);

		this.setState({
			ssnOrg: value,
			ssnOrgType: ssnOrgValidated.type,
			ssnOrgValid: ssnOrgValidated.valid ? true : null,
		});
	};

	_onSsnOrgBlur = () => this.setState({ssnOrgValid: this._validateSsnOrg(this.state.ssnOrg).valid});

	_onContinue = () => {
		if (this.state.ssnOrgValid) {
			const {actions} = this.props;

			this.setState({busy: true}, async () => {
				await delay(1000);
				const {props, state} = this;
				const {app, onLookupDataLoaded} = props;
				const {session, partner} = app;

				try {
					await session.loadLookupData({
						type: partner.allowCompany ? state.ssnOrgType : "person",
						value: state.ssnOrg.replace(/-/g, ""),
					});

					this.setState({busy: false}, onLookupDataLoaded);
				}
				catch (e) {
					this.setState({busy: false}, async () => {
						if (e.message === "REQUEST_TIMED_OUT") {
							await actions.openErrorModal({
								title: "Ett fel uppstod",
								body: "Anropet tog för lång tid. Vänligen försök igen.",
							});
						}
						else if (e.message === 'FRAUD_FLAG') {
							await actions.openErrorModal({
								title: "Personnummer spärrat",
								body: "Ditt personnummer är låst via en extern ID-spärr. Kontrollera med ditt " +
									"kreditupplysningsföretag gällande status och för att eventuellt ta bort spärren.",
							});
						}
						else {
							console.log(e.message);
						}
					});
				}
			});
		}
	}
}

// PRIVATE FUNCTIONS
function _getInitialState() {
	const partner = this.props.app.partner;
	return {
		hidden: true,
		busy: false,
		personTypeSelected: partner.allowPersonTypeChoice ? "individual" : "",
		ssnOrg: AppConfig.env === "development" ? "" : "",
		ssnOrgType: AppConfig.env === "development" ? null : null,
		ssnOrgValid: AppConfig.env === "development" ? null : null,
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Start/Start.js